import type { AdRefImageHints } from "@hightouch/lib/ad-studio/html-editor/resolve-ad-refs";
import { isSignedUrlExpired } from "src/pages/ad-studio/presigned-urls";

/**
 * Builds `AdRefImageHints.directUrls` from an ad row's `component_asset_urls`.
 * The server pre-signs exactly one rendition, the full-size webp, so the map
 * is returned only when `width`/`height`/`format` ask for that rendition; any
 * other request returns undefined and keeps resolving through `/agents/files`.
 *
 * `webpUrls` is a JSON scalar, so this is the boundary that narrows it: only
 * string values survive, and an already-expired signature is dropped so the
 * `<img>` falls back to the API instead of painting a 403.
 */
export function directUrlsForRendition({
  componentAssetUrls,
  width,
  height,
  format,
}: {
  componentAssetUrls: { webpUrls?: unknown } | null | undefined;
} & Pick<AdRefImageHints, "width" | "height" | "format">):
  | Record<string, string>
  | undefined {
  if (format !== "webp" || width !== undefined || height !== undefined) {
    return undefined;
  }
  const webpUrls = componentAssetUrls?.webpUrls;
  if (
    typeof webpUrls !== "object" ||
    webpUrls === null ||
    Array.isArray(webpUrls)
  ) {
    return undefined;
  }
  const live = Object.entries(webpUrls).filter(
    (entry): entry is [string, string] =>
      typeof entry[1] === "string" && !isSignedUrlExpired(entry[1]),
  );
  return live.length > 0 ? Object.fromEntries(live) : undefined;
}
